import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import SEO from '../../components/SEO';
import './Products.css';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const url = selectedCategory
        ? `/products?category=${selectedCategory}`
        : '/products';
      const response = await api.get(url);
      setProducts(response.data);

      // Extract unique categories
      const uniqueCategories = [...new Set(response.data.map(p => p.category).filter(Boolean))];
      setCategories(uniqueCategories);

      setError(null);
    } catch (err) {
      console.error('Failed to fetch products:', err);
      setError('Failed to load products. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (productId) => {
    try {
      await api.post('/cart/items', {
        product_id: productId,
        quantity: 1
      });
      alert('Product added to cart!');
    } catch (err) {
      if (err.response?.status === 401) {
        alert('Please login to add items to cart');
        window.location.href = '/login';
      } else {
        alert('Failed to add product to cart');
      }
    }
  };

  if (loading) return <div className="loading">Loading products...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <>
      <SEO
        title="Products"
        description="Browse our selection of quality products"
        keywords="products, shop, buy, e-commerce"
      />
      <div className="products-page">
        <div className="container">
          <div className="products-header">
            <h1>Our Products</h1>
            <p>Discover quality products at great prices</p>
          </div>

          {/* Category Filter */}
          {categories.length > 0 && (
            <div className="category-filter">
              <button
                className={`category-btn ${selectedCategory === '' ? 'active' : ''}`}
                onClick={() => setSelectedCategory('')}
              >
                All Products
              </button>
              {categories.map(category => (
                <button
                  key={category}
                  className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>
          )}

          {/* Products Grid */}
          {products.length === 0 ? (
            <div className="no-products">
              <p>No products available at the moment.</p>
            </div>
          ) : (
            <div className="products-grid">
              {products.map(product => (
                <div key={product.id} className="product-card">
                  <div className="product-image">
                    {product.image_url ? (
                      <img src={product.image_url} alt={product.name} />
                    ) : (
                      <div className="placeholder-image">
                        <span>No Image</span>
                      </div>
                    )}
                    {product.stock <= 0 && (
                      <div className="out-of-stock-badge">Out of Stock</div>
                    )}
                  </div>
                  <div className="product-info">
                    {product.category && (
                      <span className="product-category">{product.category}</span>
                    )}
                    <h3 className="product-name">{product.name}</h3>
                    {product.description && (
                      <p className="product-description">
                        {product.description.length > 100
                          ? `${product.description.substring(0, 100)}...`
                          : product.description}
                      </p>
                    )}
                    <div className="product-footer">
                      <span className="product-price">
                        ${parseFloat(product.price).toFixed(2)}
                      </span>
                      {product.stock > 0 && product.stock <= 10 && (
                        <span className="low-stock">Only {product.stock} left!</span>
                      )}
                    </div>
                    <div className="product-actions">
                      <Link
                        to={`/products/${product.id}`}
                        className="btn btn-secondary"
                      >
                        View Details
                      </Link>
                      <button
                        className="btn btn-primary"
                        onClick={() => addToCart(product.id)}
                        disabled={product.stock <= 0}
                      >
                        {product.stock <= 0 ? 'Out of Stock' : 'Add to Cart'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Products;
